/**
 * WelcomeScreen - Empty state for chat with suggestions.
 *
 * Displays when no messages exist in the current session.
 * Features animated brain icon and quick action suggestions.
 */

import { Bot, Sparkles } from 'lucide-react'

interface WelcomeSuggestion {
  icon: string
  text: string
}

interface WelcomeScreenProps {
  onSuggestionClick: (text: string) => void
  suggestions?: WelcomeSuggestion[]
}

const DEFAULT_SUGGESTIONS: WelcomeSuggestion[] = [
  { icon: '💡', text: 'Turn off all the lights' },
  { icon: '🌡️', text: "What's the temperature?" },
  { icon: '🔒', text: 'Lock all doors' },
  { icon: '⚡', text: 'Show energy usage' },
]

export function WelcomeScreen({
  onSuggestionClick,
  suggestions = DEFAULT_SUGGESTIONS,
}: WelcomeScreenProps) {
  return (
    <div className="flex flex-col items-center justify-center h-full text-center px-4">
      {/* Animated brain icon */}
      <div className="relative mb-6">
        <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-accent/20 to-accent/5 flex items-center justify-center">
          <Bot className="w-10 h-10 text-accent" />
        </div>
        <div className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-emerald-500/20 flex items-center justify-center">
          <Sparkles className="w-3 h-3 text-emerald-400" />
        </div>
      </div>

      <h2 className="text-2xl font-bold text-text mb-2">
        Welcome to Neuro-HA
      </h2>
      <p className="text-text-muted max-w-md text-sm mb-8">
        Your AI-powered home automation assistant. Ask me anything about your smart home.
      </p>

      {/* Quick action suggestions */}
      <div className="grid gap-2 w-full max-w-sm">
        {suggestions.map((suggestion, i) => (
          <button
            key={i}
            onClick={() => onSuggestionClick(suggestion.text)}
            className="flex items-center gap-3 px-4 py-3 rounded-xl bg-surface/50 hover:bg-surface border border-surface-border hover:border-accent/30 text-left text-sm text-text-secondary hover:text-text transition-all group"
          >
            <span className="text-lg group-hover:scale-110 transition-transform">
              {suggestion.icon}
            </span>
            <span>{suggestion.text}</span>
          </button>
        ))}
      </div>
    </div>
  )
}

export default WelcomeScreen
